'use client';

import React, { useState } from 'react';
import clsx from 'clsx';
import { Button } from '../button/Button';
import { Link } from '../link/Link';
import Icon from '@/components/icon/Icon';
import LanguageSwitcher from '../language-switcher/LanguageSwitcher';
import { useTranslations } from '@/lib/locale-context';
import {
  hasSubpanel,
  isPanelGroup,
  mobileNavLevelId,
  navKey,
  sortNavGroups,
} from './nav-data';
import type { NavGroup } from './types';

type MobileHeaderProps = {
  headerData?: NavGroup[] | null;
  closeMenu: () => void;
};

type MobileEntry = {
  key: string;
  title: string;
  slug?: string | null;
  /** Set when the entry drills into another level instead of linking out. */
  childLevelKey?: string;
};

type MobileLevel = {
  key: string;
  title: string;
  entries: MobileEntry[];
};

const ROOT_LEVEL = 'root';

/**
 * Flattens the navigation into every level the drill-down can show — the top
 * level, one per panel group, one per section with its own links — so all of
 * them can be rendered up front.
 */
const buildLevels = (groups: NavGroup[]): MobileLevel[] => {
  const root: MobileLevel = { key: ROOT_LEVEL, title: '', entries: [] };
  const levels: MobileLevel[] = [root];

  groups.forEach((group, groupIndex) => {
    const groupKey = navKey(group.title, groupIndex);

    if (!isPanelGroup(group)) {
      root.entries.push({
        key: groupKey,
        title: group.title,
        slug: group.slug,
      });
      return;
    }

    const groupLevel: MobileLevel = {
      key: groupKey,
      title: group.title,
      entries: [],
    };
    root.entries.push({
      key: groupKey,
      title: group.title,
      childLevelKey: groupKey,
    });

    (group.items ?? []).forEach((item, itemIndex) => {
      const itemKey = `${groupKey}-${navKey(item.title, itemIndex)}`;

      if (!hasSubpanel(item)) {
        groupLevel.entries.push({
          key: itemKey,
          title: item.title,
          slug: item.slug,
        });
        return;
      }

      groupLevel.entries.push({
        key: itemKey,
        title: item.title,
        childLevelKey: itemKey,
      });
      levels.push({
        key: itemKey,
        title: item.title,
        entries: (item.clickedItems ?? []).map((link, linkIndex) => ({
          key: `${itemKey}-${navKey(link.title, linkIndex)}`,
          title: link.title,
          slug: link.slug,
        })),
      });
    });

    levels.push(groupLevel);
  });

  return levels;
};

/**
 * Mobile drill-down navigation.
 *
 * Every level is rendered with the page and hidden until the visitor drills
 * into it, instead of being built from state on each tap. The visible result
 * is the same one-level-at-a-time menu, but the deep links are in the served
 * HTML like the desktop panels' are (WB-813), and each drill button announces
 * the level it opens through `aria-expanded` / `aria-controls`.
 */
const MobileHeader: React.FC<MobileHeaderProps> = ({
  headerData,
  closeMenu,
}) => {
  const [stack, setStack] = useState<string[]>([]);
  const { locale, dict } = useTranslations();
  const levels = buildLevels(sortNavGroups(headerData));

  const activeKey = stack[stack.length - 1] ?? ROOT_LEVEL;
  const activeLevel =
    levels.find((level) => level.key === activeKey) ?? levels[0];

  const drillInto = (levelKey: string) =>
    setStack((previous) => [...previous, levelKey]);

  const handleBackClick = () => setStack((previous) => previous.slice(0, -1));

  return (
    <div className="mobile-header block h-full bg-white lg:hidden">
      {/* Back Button */}
      {activeLevel.key !== ROOT_LEVEL && (
        <div className="back-btn flex w-full items-center justify-between !bg-white px-4 pb-2 pt-7 lg:hidden">
          <Button
            name={activeLevel.title}
            leftIcon={{ name: 'chevron-left', color: '#07314C', size: 24 }}
            onClick={handleBackClick}
            className="w-full !bg-white !p-0 text-[20px] font-normal !text-black hover:underline hover:underline-offset-4"
            style={{ justifyContent: 'flex-start' }}
          />
        </div>
      )}

      {/* Every level is in the markup; only the active one is shown. */}
      <section className="secondRow flex max-h-[80%] w-full flex-col justify-between overflow-y-scroll bg-white p-4 pb-10">
        {levels.map((level) => {
          const isActive = level.key === activeLevel.key;

          return (
            <nav
              key={level.key}
              id={mobileNavLevelId(level.key)}
              aria-label={level.title || undefined}
              hidden={!isActive}
              className={clsx(
                level.key === ROOT_LEVEL
                  ? 'smzh-navbar w-full'
                  : 'sublink-navbar h-full w-full bg-white',
                !isActive && 'hidden',
              )}
            >
              <ul className="flex flex-col gap-4">
                {level.entries.map((entry) => (
                  <li
                    key={entry.key}
                    className="flex w-full flex-col hover:bg-brand-100"
                  >
                    {entry.childLevelKey ? (
                      <button
                        type="button"
                        aria-expanded={stack.includes(entry.childLevelKey)}
                        aria-controls={mobileNavLevelId(entry.childLevelKey)}
                        className="flex w-full flex-row justify-between p-3 hover:bg-brand-100"
                        onClick={() => drillInto(entry.childLevelKey as string)}
                      >
                        <span className="text-base !text-brand-700">
                          {entry.title}
                        </span>
                        <Icon className="text-brand-700" name="arrow-right" />
                      </button>
                    ) : (
                      <Link
                        href={entry.slug || `/${locale}`}
                        onClick={closeMenu}
                        className="!p-3 text-base !text-brand-700 no-underline hover:no-underline focus:no-underline active:no-underline"
                      >
                        <span className="text-base !text-brand-700">
                          {entry.title}
                        </span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          );
        })}
      </section>

      {/* Bottom Button */}
      <div
        className="fixed bottom-0 flex w-full border-t border-neutral-50 !bg-white"
        style={{
          paddingBottom: `env(safe-area-inset-bottom)`,
        }}
      >
        <div className="flex h-full w-full items-center justify-center py-4 max-lg:justify-between max-lg:px-5">
          <Link
            onClick={closeMenu}
            title={dict.terminCTA.link.title}
            href={dict.terminCTA.link.href}
            variant={dict.terminCTA.link.variant}
            rightIcon={dict.terminCTA.link.rightIcon}
            className={dict.terminCTA.link.className}
          />
          <LanguageSwitcher currentLocale={locale} />
        </div>
      </div>
    </div>
  );
};

export default MobileHeader;
